import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Accessibility20Filled,
  Add20Filled,
  ArrowLeft24Filled,
  ArrowRight24Filled,
  ArrowUpload20Filled,
  ChevronDown12Regular
} from "@fluentui/react-icons";
import classNames from "classnames";
import { getTabs } from "../../utils/artwork-utils";
import { TabGroup } from "../layout/TabGroup";
import { FullscreenLayout } from "../layout/FullscreenLayout";
import { Column } from "../layout/Column";
import { MediaGrid } from "./MediaGrid";
import {
  Button,
  makeStyles,
  Menu,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SearchBox
} from "@fluentui/react-components";

const useStyles = makeStyles({
  actionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "var(--fontSizeBase500, 20px)",
    alignItems: "center",
    margin: "20px 0px",
    "& > :first-child": {
      flexGrow: 2
    }
  },
  avatarsContainer: {},
  pager: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  desktopOnly: {
    display: "none",
    "@media (min-width: 576px)": {
      display: "initial"
    }
  },
  mobileOnly: {
    display: "initial",
    "@media (min-width: 576px)": {
      display: "none"
    }
  }
});

export function MediaBrowser({
  onClose,
  browserRef,
  searchInputRef,
  searchPlaceholder,
  searchDescription,
  onSearchKeyDown,
  onClearSearch,
  mediaSources,
  selectedSource,
  query,
  onSelectFacet,
  facets,
  onSelectSource,
  onChangeQuery,
  hasNext,
  hasPrevious,
  onNextPage,
  onPreviousPage,
  noResultsMessage,
  children,
  activeFilter,
  filterItems,
  onChangeFilterDropdown,
  inlineLoader,
  avatarPreview,
  onCreateAvatar,
  loading
}) {
  const intl = useIntl();
  const [selectedFilter, setSelectedFilter] = useState("featured");
  const [selectedDropdownFilter, setSelectedDropdownFilter] = useState(null);

  const placeArtTitles = [
    { id: "collections", title: "National Gallery of Art" },
    { id: "met", title: "The Metropolitan Museum of Art" },
    { id: "art-projects", title: "Art Projects" }
  ];

  const [marketplaceTabs, setMarketplaceTabs] = useState(placeArtTitles);

  const changeTabs = async () => {
    const results = await getTabs();
    const filteredTabs = marketplaceTabs.filter(filter => results.includes(filter.id));
    setMarketplaceTabs(filteredTabs);
  };

  useEffect(() => {
    if (facets && facets.length) {
      setSelectedFilter(facets[0].params.filter);
    }
  }, [facets]);

  useEffect(() => {
    changeTabs();
  }, []);

  const myMediaList = [
    {
      id: "placeart",
      text: intl.formatMessage({
        defaultMessage: "Public Domain Art",
        id: "media-browser.media.place-art"
      }),
      params: { filter: "" }
    },
    {
      id: "sketchfab",
      text: intl.formatMessage({
        defaultMessage: "Community Media",
        id: "media-browser.media.community"
      }),
      params: { filter: "" }
    }
  ];

  const navTitles = [
    { id: "sketchfab", title: "3D Model" },
    { id: "images", title: "Image" },
    { id: "gifs", title: "GIF" },
    { id: "videos", title: "Video" }
  ];

  const filteredNavTitles = navTitles.filter(obj => mediaSources.includes(obj.id));

  const onSelectFacetIndex = index => {
    setSelectedFilter(facets[index].params.filter);
    onSelectFacet(facets[index]);
    setSelectedDropdownFilter(null);
  };

  const onSelectPublicArt = event => {
    const index = placeArtTitles.map(item => item.id).indexOf(event.target.value);

    onSelectFacetIndex(index);
  };

  const onSelectSourceMedia = index => {
    const sourceSelected = mediaSources.filter(source => source === myMediaList[index].id)[0];

    onSelectSource(sourceSelected);
  };

  const onSelectDropdown = e => {
    const dropdownElSelected = e.target.value;
    setSelectedDropdownFilter(dropdownElSelected);
    onChangeFilterDropdown(dropdownElSelected);
  };

  const onSelectSourceIndex = index => {
    const sourceSelected = mediaSources.filter(source => source === filteredNavTitles[index].id);
    onSelectSource(sourceSelected[0]);
  };

  const onChangeField = e => {
    onChangeQuery(e, selectedFilter);
  };

  const onClearField = () => {
    onClearSearch(selectedFilter);
  };

  const titleHeader = {
    avatars: {
      header: "Avatars",
      headerId: "media-browser.nav_title.avatars"
    },
    sketchfab: {
      header: "Media",
      headerId: "media-browser.nav_title.media"
    },
    images: {
      header: "Images",
      headerId: "media-browser.nav_title.images"
    },
    gifs: {
      header: "Media",
      headerId: "media-browser.nav_title.media"
    },
    videos: {
      header: "Media",
      headerId: "media-browser.nav_title.media"
    },
    scenes: {
      header: "Scenes",
      headerId: "media-browser.nav_title.scenes"
    },
    placeart: {
      header: "Public Domain Art",
      headerId: "media-browser.nav_title.placeart"
    }
  };

  const styles = useStyles();

  return (
    <FullscreenLayout
      loading={loading}
      open={true}
      setOpen={onClose}
      contentClassName={selectedSource === "avatars" ? styles.avatarFullscreen : ""}
      headerCenter={
        <>
          {intl.formatMessage({
            id: titleHeader[selectedSource] ? titleHeader[selectedSource].headerId : "media-browser.nav_title.avatars",
            defaultMessage: titleHeader[selectedSource] ? titleHeader[selectedSource].header : ""
          })}
        </>
      }
    >
      <div
        className={classNames(
          styles.avatarsContainer,
          selectedSource === "avatars" ? "avatar-container" : "",
          !!avatarPreview && selectedSource === "avatars" ? styles.avatarsContainerPreview : ""
        )}
      >
        <div className={selectedSource === "avatars" ? styles.avatarGridContainer : ""}>
          {mediaSources && (
            <div>
              <div>
                {selectedSource !== "avatars" && selectedSource !== "scenes" && (
                  <TabGroup
                    theme="primary"
                    activeFilter={activeFilter}
                    list={myMediaList.map(mediaList => mediaList.text)}
                    onSelectFacetIndex={onSelectSourceMedia}
                  />
                )}

                {(selectedSource === "avatars" || selectedSource === "scenes") && (
                  <TabGroup
                    theme="primary"
                    list={facets.map(facet => facet.text)}
                    activeFilter={facets.find(facet => facet.params.filter == activeFilter)?.text || activeFilter}
                    onSelectFacetIndex={onSelectFacetIndex}
                  />
                )}
                <div className={styles.actionsContainer}>
                  <SearchBox
                    value={query}
                    onChange={e => onChangeField(e)}
                    appearance="filled-darker"
                    onKeyDown={onSearchKeyDown}
                    ref={searchInputRef}
                    placeholder={searchPlaceholder}
                    description={searchDescription}
                    className={classNames(styles.searchInput, styles.desktop, "text-input-tab")}
                  />
                  {selectedSource === "avatars" && activeFilter === "my-avatars" && (
                    <Menu>
                      <MenuTrigger disabled={loading} disableButtonEnhancement>
                        <Button disabled={loading} appearance="primary" icon={<Add20Filled />}>
                          <span className={styles.desktopOnly}>
                            <FormattedMessage id="more-menu.avatar" defaultMessage="Avatar" />
                          </span>
                          <span className={styles.mobileOnly}>
                            <FormattedMessage id="more-menu.avatar-mobile" defaultMessage="Add" />
                          </span>
                          &nbsp;
                          <ChevronDown12Regular />
                        </Button>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuGroup>
                            <MenuGroupHeader>
                              <FormattedMessage id="avatar-editor.create" defaultMessage="Create" />
                            </MenuGroupHeader>
                            <MenuItem disabled={loading} icon={<Accessibility20Filled />} onClick={onCreateAvatar}>
                              <FormattedMessage id="avatar-editor.title" defaultMessage="Avatar" />
                            </MenuItem>
                          </MenuGroup>
                          <MenuGroup>
                            <MenuGroupHeader>
                              <FormattedMessage id="place-popover.item-type.upload" defaultMessage="Upload" />
                            </MenuGroupHeader>
                            <MenuItem
                              disabled={loading}
                              icon={<ArrowUpload20Filled />}
                              onClick={() => onCreateAvatar("upload")}
                            >
                              <FormattedMessage
                                id="avatar-editor.create-custom-avatar"
                                defaultMessage="Custom Avatar"
                              />
                            </MenuItem>
                          </MenuGroup>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  )}
                </div>
              </div>

              <div>
                {selectedSource !== "avatars" && selectedSource !== "scenes" && selectedSource !== "placeart" && (
                  <div className={classNames(styles.buttonNav, styles.buttonNavSearch)}>
                    <TabGroup
                      theme="seccondary"
                      list={filteredNavTitles.map(filter => filter.title)}
                      onSelectFacetIndex={onSelectSourceIndex}
                    />
                  </div>
                )}
                {selectedSource === "placeart" && (
                  <div className={classNames(styles.buttonNav, styles.buttonNavSearch)}>
                    <div className={styles.dropdown}>
                      <select onChange={onSelectPublicArt} className="publicart--providers">
                        <>
                          {marketplaceTabs.map(item => (
                            <option value={item.id} key={item.id}>
                              {item.title}
                            </option>
                          ))}
                        </>
                      </select>
                    </div>
                    {filterItems && filterItems.length > 0 && (
                      <div className={styles.dropdown}>
                        <select onChange={onSelectDropdown}>
                          {selectedFilter != "placeart-artists" && selectedFilter != "art-projects" && (
                            <option selected={selectedDropdownFilter == null} value="">
                              {intl.formatMessage({
                                id: "media-browser.dropdown.all-collections",
                                defaultMessage: "All Collections"
                              })}
                            </option>
                          )}

                          {selectedFilter && (
                            <>
                              {filterItems.map(item => (
                                <option value={item.id} key={item.id}>
                                  {item.name}
                                </option>
                              ))}
                            </>
                          )}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* <div className={classNames(styles.tabContainer)}>
                  <TextInputField
                    value={query}
                    onChange={onChangeField}
                    autoFocus={autoFocusSearch}
                    ref={searchInputRef}
                    placeholder={searchPlaceholder}
                    onKeyDown={onSearchKeyDown}
                    beforeInput={<SearchIcon className={styles.searchIcon} />}
                    afterInput={
                      <IconButton onClick={onClearField}>
                        <CloseIcon height={16} width={16} />
                      </IconButton>
                    }
                    description={searchDescription}
                    className={classNames(styles.searchInput, styles.mobile, "text-input-tab")}
                  />
                </div> */}
              </div>
            </div>
          )}
          <div className={styles.content}>
            {inlineLoader ? inlineLoader : null}
            <Column grow ref={browserRef}>
              {children ? (
                <MediaGrid
                  isVariableWidth={selectedSource === "gifs" || selectedSource === "images"}
                  sm={selectedSource === "avatars"}
                >
                  {children}
                </MediaGrid>
              ) : (
                <div className={styles.noResults}>{noResultsMessage}</div>
              )}
              {(hasNext || hasPrevious) && (
                <div className={styles.pager}>
                  <Button disabled={!hasPrevious} onClick={onPreviousPage} preset="primary" appearance="transparent">
                    <ArrowLeft24Filled />
                  </Button>

                  <Button disabled={!hasNext} onClick={onNextPage} preset="primary" appearance="transparent">
                    <ArrowRight24Filled />
                  </Button>
                </div>
              )}
            </Column>
          </div>
        </div>
      </div>
    </FullscreenLayout>
  );
}

MediaBrowser.propTypes = {
  onClose: PropTypes.func,
  browserRef: PropTypes.any,
  searchInputRef: PropTypes.any,
  autoFocusSearch: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchDescription: PropTypes.node,
  onSearchKeyDown: PropTypes.func,
  onClearSearch: PropTypes.func,
  mediaSources: PropTypes.array,
  selectedSource: PropTypes.string,
  onSelectSource: PropTypes.func,
  activeFilter: PropTypes.string,
  facets: PropTypes.array,
  onSelectFacet: PropTypes.func,
  query: PropTypes.string,
  onChangeQuery: PropTypes.func,
  headerRight: PropTypes.node,
  hasNext: PropTypes.bool,
  hasPrevious: PropTypes.bool,
  onNextPage: PropTypes.func,
  onPreviousPage: PropTypes.func,
  noResultsMessage: PropTypes.node,
  children: PropTypes.node,
  filterItems: PropTypes.array,
  onChangeFilterDropdown: PropTypes.func,
  inlineLoader: PropTypes.node,
  onCreateAvatar: PropTypes.func,
  avatarPreview: PropTypes.node,
  loading: PropTypes.bool
};

MediaBrowser.defaultProps = {
  noResultsMessage: "No Results"
};
